import React, { useState, useEffect, useRef, useCallback } from 'react';
import { navigateTo } from '@/Utils/routing';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FlagIcon from '@mui/icons-material/Flag';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CircularProgress from '@mui/material/CircularProgress';

import PostCard from '@/Components/social/PostCard';
import PostReply from '@/Components/social/PostReply';
import LoadingView from '@/Components/social/LoadingView';
import { usePosts, } from '@/Recoil/Social';
import * as PostService from '@/Data/Social';
import { contentFilter } from '@/Data/Social/ContentFilter';
import type { Post } from '@/Data/Social';

import './postDetail.scss';

const PostDetail = () => {
    // Get postid from URL path
    const pathParts = window.location.pathname.split('/');
    const postid = pathParts[pathParts.length - 1]; // Get last part of path
    
    // State hooks - matching original exactly
    const { posts, syncPost, incrementViewCount } = usePosts();
    
    // Get post from state (complete data with counts)
    const post = postid ? posts[postid] || null : null;
    
    // Hide the post if it's been reported
    const isPostFiltered = post && (
        contentFilter.isPostFiltered(post.postid) || 
        contentFilter.isUserFiltered(post.addressid) ||
        contentFilter.hasFilteredHashtags(post.hashtags)
    );
    
    // Local state - matching original exactly
    const [childPosts, setChildPosts] = useState<Post[]>([]);
    const [loading, setLoading] = useState(true);
    const [commentText, setCommentText] = useState('');
    const [submittingComment, setSubmittingComment] = useState(false);
    
    // Image upload functionality (simplified for web)
    const [commentImages, setCommentImages] = useState<Array<{
        localIdentifier: string;
        uri: string;
        s3Url?: string;
        uploadState: 'waiting' | 'uploaded';
    }>>([]);
    
    const scrollViewRef = useRef<HTMLDivElement>(null);
    const commentInputRef = useRef<HTMLTextAreaElement>(null);
    
    const loadPostData = useRef(async () => {
        if (!postid) return;
        
        setLoading(true);
        let postData: Post | null = posts[postid] || null;
        try {
            // If not in state, fetch from API
            if (!posts[postid]) {
                postData = await syncPost({postid});
            } else {
                syncPost({ postid }); // Sync to ensure latest data
            }
            
            // Check if post exists after sync
            if (postData) {
                // Load child posts (replies)
                incrementViewCount(postid); // Increment view count
                loadChildPosts();
            } else {
                alert('Post not found');
                navigateTo('/social');
            }
        } catch (error) {
            console.error('Failed to load post:', error);
            alert('Failed to load post');
            navigateTo('/social');
        } finally {
            setLoading(false);
        }
    });
    
    // Load post data when component mounts or when engagement data changes
    useEffect(() => {
        if (postid) {
            loadPostData.current();
        }
    }, [postid]);
    
    const loadChildPosts = useCallback(async () => {
        if (!postid) return;
        
        try {
            const childPostsData = await PostService.listPosts({parent_postid: postid});
            if (childPostsData && childPostsData) {
                // Filter out reported child posts
                const filteredChildPosts = childPostsData.filter(childPost => 
                    !contentFilter.isPostFiltered(childPost.postid) && 
                    !contentFilter.isUserFiltered(childPost.addressid) &&
                    !contentFilter.hasFilteredHashtags(childPost.hashtags)
                );
                setChildPosts(filteredChildPosts);
            }
        } catch (error) {
            console.error('Failed to load child posts:', error);
            // Fallback to empty child posts array
            setChildPosts([]);
        }
    }, [postid]);
    
    const handleSubmitComment = async () => {
        if ((!commentText.trim() && commentImages.length === 0) || submittingComment || !postid) return;
        
        setSubmittingComment(true);
        try {
            // Get S3 URLs from uploaded images
            const imageKeys: string[] = commentImages
                .filter(image => image.uploadState === 'uploaded' && image.s3Url)
                .map(image => image.s3Url!);
            
            const newChildPost = await PostService.createPost({
                content: commentText.trim(),
                parent_postid: postid,
                media: imageKeys.length > 0 ? imageKeys : undefined
            });
            
            if (newChildPost) {
                setChildPosts(prev => [...prev, newChildPost]);
                setCommentText('');
                // Clear images after successful comment
                setCommentImages([]);
                commentInputRef.current?.blur();
                
                // Scroll to bottom to show new comment
                setTimeout(() => {
                    scrollViewRef.current?.scrollTo({ top: scrollViewRef.current.scrollHeight, behavior: 'smooth' });
                }, 100);
            } else {
                alert('Failed to submit comment');
            }
        } catch (error) {
            console.error('Failed to submit comment:', error);
            alert('Failed to submit comment');
        } finally {
            setSubmittingComment(false);
        }
    };

    const handleReplyToUser = (addressid: string) => {
        setCommentText(`@${addressid} `);
        commentInputRef.current?.focus();
    };
    
    const handleDeleteReply = async (replyId: string) => {
        try {
            const result = await PostService.deletePost({ postid: replyId });
            if (result) {
                setChildPosts(prev => prev.filter(childPost => childPost.postid !== replyId));
            } else {
                alert('Failed to delete reply');
            }
        } catch (error) {
            console.error('Failed to delete reply:', error);
            alert('Failed to delete reply');
        }
    };
    
    const handleReportReply = (replyId: string) => {
        navigateTo(`/social/report?postid=${replyId}`);
    };
    
    const addImages = (files: File[]) => {
        const newImages = files.map((file, index) => ({
            localIdentifier: `new-${Date.now()}-${index}`,
            uri: URL.createObjectURL(file),
            uploadState: 'waiting' as const
        }));
        setCommentImages(prev => [...prev, ...newImages]);
        // TODO: Implement S3 upload
    };
    
    const removeImage = (localIdentifier: string) => {
        setCommentImages(prev => prev.filter(img => img.localIdentifier !== localIdentifier));
    };
    
    if (loading) {
        return <LoadingView />;
    }
    
    if (!post || isPostFiltered) {
        return (
            <div className="PostDetail">
                <div className="SafeAreaView Container">
                <div className="View Header">
                    <button 
                        className="Pressable BackButton"
                        onClick={() => navigateTo('/social')}
                    >
                        <ArrowBackIcon />
                    </button>
                    <h1 className="Text HeaderTitle">Post</h1>
                </div>
                <div className="View ErrorContainer">
                    <p className="Text ErrorText">
                        {!post ? 'Post not found' : 'Content not available'}
                    </p>
                </div>
            </div>
            </div>
        );
    }
    
    return (
        <div className="PostDetail">
            <div className="SafeAreaView Container">
            {/* Comment Input */}
            <div className="KeyboardAvoidingView">
                {/* Header */}
                <div className="View Header">
                    <button 
                        className="Pressable BackButton"
                        onClick={() => navigateTo('/social')}
                    >
                        <ArrowBackIcon />
                    </button>
                    <button 
                        className="Pressable ReportButton"
                        onClick={() => {
                            if (postid) {
                                navigateTo(`/social/report?postid=${postid}`);
                            }
                        }}
                    >
                        <FlagIcon />
                    </button>
                </div>
                
                <div className="View ContentWrapper">
                    {/* Content */}
                    <div 
                        ref={scrollViewRef}
                        className="ScrollView Content"
                        onScroll={(e) => {
                            // Pull to refresh simulation
                            if (e.currentTarget.scrollTop === 0) {
                                // handleRefresh();
                            }
                        }}
                    >
                        
                        {/* Main Post */}
                        <div className="View PostContainer">
                            <PostCard post={post} />
                        </div>
                        
                        {/* Separator */}
                        <div className="View Separator" />
                        
                        {/* Comments Section */}
                        <div className="View CommentsSection">
                            <h2 className="Text CommentsTitle">Reply</h2>
                            
                            <div className="View CommentsList">
                                {childPosts.map((childPost, index) => (
                                    <React.Fragment key={childPost.postid}>
                                        <PostReply
                                            reply={childPost}
                                            onDelete={handleDeleteReply}
                                            onReport={handleReportReply}
                                            onReply={handleReplyToUser}
                                        />
                                        {index < childPosts.length - 1 && (
                                            <div className="View CommentSeparator" />
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>
                
                    <div className="View CommentInputContainer">
                        {/* Image Preview Section */}
                        {commentImages.length > 0 && (
                            <div className="View ImagePreviewSection">
                                <div className="ScrollView ImagePreviewScroll">
                                    <div className="View ImagePreviewContainer">
                                        {commentImages.map((imageItem) => (
                                            <div key={imageItem.localIdentifier} className="View ImagePreviewWrapper">
                                                <img src={imageItem.uri} className="FastImage PreviewImage" alt="" />
                                                <button 
                                                    className="Pressable RemoveImageButton"
                                                    onClick={() => removeImage(imageItem.localIdentifier)}
                                                >
                                                    <CloseIcon />
                                                </button>
                                                {imageItem.uploadState === 'waiting' && (
                                                    <div className="View UploadingOverlay">
                                                        <CircularProgress size={16} />
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}
                        
                        {/* Text Input Row */}
                        <div className="View CommentInputWrapper">
                            <textarea
                                ref={commentInputRef}
                                className="TextInput CommentInput"
                                placeholder="Write a comment..."
                                value={commentText}
                                onChange={(e) => setCommentText(e.target.value)}
                                maxLength={500}
                            />
                            
                            {/* Add Images Button */}
                            <label className="NewImageButton">
                                <input
                                    type="file"
                                    multiple
                                    accept="image/*"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                        const files = Array.from(e.target.files || []);
                                        if (files.length + commentImages.length > 10) {
                                            alert('You can only add up to 10 images');
                                            return;
                                        }
                                        addImages(files);
                                    }}
                                />
                                <AddPhotoAlternateIcon />
                            </label>
                            
                            <button
                                className={`Pressable SendButton ${((!commentText.trim() && commentImages.length === 0) || submittingComment) ? 'SendButtonDisabled' : ''}`}
                                onClick={handleSubmitComment}
                                disabled={(!commentText.trim() && commentImages.length === 0) || submittingComment}
                            >
                                {submittingComment ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <SendIcon />
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>
    );
};

export default PostDetail;